import { useForm } from "@tanstack/react-form";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import type { CaseFileLite } from "@/briefpoint-client";
import PdfViewerModal from "@/components/PdfViewerModal";
import SearchInput from "@/components/SearchInput";
import { useCaseFileApi } from "@/hooks/useApi";
import { SEARCH_PARAMS } from "@/lib/constants";
import { getCaseFilesWithheldListOptions } from "@/lib/query-options";
import { updateProductionTabParams } from "@/lib/utils";
import WithheldFileItem from "./WithheldFileItem";
import styles from "./WithheldFileLists.module.scss";

const WithHeldFileLists = () => {
  const caseFileApi = useCaseFileApi();
  const { id: caseId } = useParams<{ id: string }>();
  const history = useHistory();
  const location = useLocation();
  const [selectedFile, setSelectedFile] = useState<string | null>(null);

  // QUERY
  const readUrlQuery = useQuery({
    queryKey: ["caseFileApi.caseFileGetReadUrl", selectedFile, caseId],
    enabled: !!selectedFile,
    queryFn: async () => {
      const data = await caseFileApi.caseFileGetReadUrl({
        fileId: selectedFile || "",
        caseId,
      });
      return JSON.parse(data);
    },
  });

  const caseFilesWithheldListQuery = useQuery({
    ...getCaseFilesWithheldListOptions({
      caseFileApi,
      caseId,
    }),
    enabled: !!caseId,
    select: (data) => {
      return {
        privileged: data.filter((item) => item.isPrivileged) || [],
        withheld:
          data.filter((item) => item.isWithheld && !item.isPrivileged) || [],
      };
    },
  });

  // FORM
  const form = useForm({
    defaultValues: {
      searchText:
        new URLSearchParams(location.search).get(
          SEARCH_PARAMS.WITHHELD_SEARCH,
        ) || "",
    },
    listeners: {
      onChangeDebounceMs: 500,
      onChange: ({ formApi }) => {
        // Auto submit when canSubmit is true
        if (formApi.state.canSubmit) {
          void formApi.handleSubmit();
        }
      },
    },
    onSubmit: async () => {
      const navigationParams = updateProductionTabParams(
        location.search,
        {
          tab: SEARCH_PARAMS.PRODUCTION,
          produceSearch:
            new URLSearchParams(location.search).get(SEARCH_PARAMS.SEARCH) ||
            undefined,
          withheldSearch: form.state.values.searchText,
        },
        caseId || "",
      );

      // Used for submitting the search text for withheld files - push used to allow user to navigate in/out of those parameters when set
      history.push(navigationParams);
    },
  });

  // FILTERS
  // Filter files based on search query when the withheld tab is active
  const filterFiles = (files: CaseFileLite[], searchQuery: string) => {
    // Then filter by search query if provided
    if (!searchQuery.trim()) return files;

    const query = searchQuery.toLowerCase();
    return files.filter((file) => file.name?.toLowerCase().includes(query));
  };

  const filteredPrivileged = filterFiles(
    caseFilesWithheldListQuery.data?.privileged || [],
    form.state.values.searchText,
  );
  const filteredWithheld = filterFiles(
    caseFilesWithheldListQuery.data?.withheld || [],
    form.state.values.searchText,
  );

  const hasNoFiles =
    filteredPrivileged.length === 0 && filteredWithheld.length === 0;

  return (
    <>
      <PdfViewerModal
        url={readUrlQuery.data}
        onClose={() => setSelectedFile(null)}
      />
      <div className="mb-3 pt-3 pb-1">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.Field
            name="searchText"
            children={(field) => (
              <SearchInput
                fixed={true}
                value={field.state.value}
                onChange={(value) => field.handleChange(value)}
                placeholder="Search Documents"
                size="sm"
              />
            )}
          />
        </form>
      </div>
      {hasNoFiles && <p>No files found.</p>}
      {filteredPrivileged.length > 0 && (
        <>
          <h5
            data-testid="privileged-withheld-section-title"
            className={styles.withheldFileListSectionTitle}
          >
            Privileged Withheld
          </h5>
          {filteredPrivileged.map((file) => (
            <WithheldFileItem
              key={file.id}
              file={file}
              onSelect={setSelectedFile}
            />
          ))}
        </>
      )}
      {filteredWithheld.length > 0 && (
        <>
          <h5
            data-testid="other-withheld-section-title"
            className={`${styles.withheldFileListSectionTitle} mt-4`}
          >
            Other Withheld
          </h5>
          {filteredWithheld.map((file) => (
            <WithheldFileItem
              key={file.id}
              file={file}
              onSelect={setSelectedFile}
            />
          ))}
        </>
      )}
    </>
  );
};

export default WithHeldFileLists;
